/**
 * PipelineRunsTab — per-job latest-run status (progress bars) plus a flat
 * run-history table across every job in the pipeline (newest first).
 *
 * Rendered from the top-level "Pipeline Runs" tab (see
 * PipelineRunsDashboard), which owns the pipeline picker. Progress-bar
 * detail previously lived in its own "Pipeline Details" tab; that tab was
 * removed and its content merged in here per request.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import { EmptyState } from "@/shared/components/platform/EmptyState";
import type { PEJob, PEJobRun } from "../../features/pipeline-engineer/types";
import {
  listJobs,
  listPipelineRuns,
} from "../../features/pipeline-engineer/api/pe-client";
import {
  formatDuration,
  formatRelativeTime,
  runStatusStyle,
} from "../../features/pipeline-engineer/runFormatting";
import { JobProgressBar } from "./JobProgressBar";

/** Non-terminal statuses — this tab polls run history faster while any exist. */
const ACTIVE_STATUSES = new Set<string>(["queued", "running"]);
const POLL_INTERVAL_MS = 3000;
/**
 * Baseline poll cadence used even when nothing is locally known to be in
 * flight — a cron-scheduled run can start at any moment with nobody having
 * clicked anything, so polling can't be gated on "active run already
 * observed" alone.
 */
const IDLE_POLL_INTERVAL_MS = 20000;

interface PipelineRunsTabProps {
  pipelineId: string;
  /** Bump to force a re-fetch (e.g. after Run All is triggered on the Jobs tab). */
  refreshKey?: number;
}

export function PipelineRunsTab({
  pipelineId,
  refreshKey = 0,
}: PipelineRunsTabProps) {
  const [jobs, setJobs] = useState<PEJob[]>([]);
  const [runs, setRuns] = useState<PEJobRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [jobData, runData] = await Promise.all([
        listJobs(pipelineId),
        listPipelineRuns(pipelineId),
      ]);
      setJobs(jobData);
      setRuns(runData);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to load pipeline runs",
      );
    } finally {
      setLoading(false);
    }
  }, [pipelineId]);

  const refetchRuns = useCallback(async () => {
    try {
      setRuns(await listPipelineRuns(pipelineId));
    } catch {
      // Best-effort — this tick's data is simply stale; fetchAll() surfaces
      // its own errors for the initial load.
    }
  }, [pipelineId]);

  useEffect(() => {
    void fetchAll();
  }, [fetchAll, refreshKey]);

  const jobNames = useMemo(
    () => Object.fromEntries(jobs.map((j: PEJob) => [j.id, j.jobName])),
    [jobs],
  );

  // Runs are ordered most-recent-first (FR46) — first occurrence per
  // job_id is that job's latest run.
  const runsByJobId = useMemo(() => {
    const latest: Record<string, PEJobRun> = {};
    for (const run of runs) {
      if (!(run.jobId in latest)) {
        latest[run.jobId] = run;
      }
    }
    return latest;
  }, [runs]);

  // Keep polling run history while this tab is mounted — faster while a run
  // is actively in flight, slower otherwise — so scheduled runs surface on
  // their own.
  useEffect(() => {
    const hasActiveRun = Object.values(runsByJobId).some((run) =>
      ACTIVE_STATUSES.has(run.status),
    );
    const intervalMs = hasActiveRun ? POLL_INTERVAL_MS : IDLE_POLL_INTERVAL_MS;
    const intervalId = setInterval(() => void refetchRuns(), intervalMs);
    return () => clearInterval(intervalId);
  }, [runsByJobId, refetchRuns]);

  if (loading) {
    return (
      <div className="glass-card rounded-2xl p-4 flex items-center justify-center py-10">
        <Loader2
          size={20}
          className="animate-spin text-white/30"
          aria-hidden="true"
        />
      </div>
    );
  }

  if (error !== null) {
    return (
      <div className="glass-card rounded-2xl p-4">
        <EmptyState
          variant="system-issue"
          heading="Failed to load pipeline runs"
          description={error}
          primaryAction={{ label: "Retry", onClick: () => void fetchAll() }}
        />
      </div>
    );
  }

  if (jobs.length === 0) {
    return (
      <div className="glass-card rounded-2xl p-4">
        <EmptyState
          variant="first-visit"
          heading="No jobs yet"
          description="Add a job to this pipeline to see its run status and history here."
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Latest status per job */}
      <div className="glass-card rounded-2xl p-4 space-y-3">
        <h3 className="text-xs font-semibold text-white/50 uppercase tracking-wide">
          Latest Run Status
        </h3>
        {jobs.map((job) => (
          <div
            key={job.id}
            className="px-3 py-2.5 rounded-lg border border-white/5 bg-white/[0.02]"
          >
            <div className="flex items-center gap-2 flex-wrap mb-1.5">
              <span className="text-sm font-medium text-white">
                {job.jobName}
              </span>
              <span className="text-xs text-white/40">{job.targetTable}</span>
            </div>
            <JobProgressBar run={runsByJobId[job.id]} />
          </div>
        ))}
      </div>

      {/* Run history table */}
      <div className="glass-card rounded-2xl p-4 overflow-x-auto">
        <h3 className="text-xs font-semibold text-white/50 uppercase tracking-wide mb-3">
          Run History
        </h3>
        {runs.length === 0 ? (
          <EmptyState
            variant="first-visit"
            heading="No runs yet"
            description="Run a job from the Jobs tab to see its history here."
          />
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-white/40 border-b border-white/10">
                <th className="py-2 pr-4 font-medium">Job</th>
                <th className="py-2 pr-4 font-medium">Status</th>
                <th className="py-2 pr-4 font-medium">Started</th>
                <th className="py-2 pr-4 font-medium">Duration</th>
                <th className="py-2 pr-4 font-medium">Rows Loaded</th>
                <th className="py-2 pr-4 font-medium">Error</th>
              </tr>
            </thead>
            <tbody>
              {runs.map((run) => (
                <tr
                  key={run.id}
                  className="border-b border-white/5 last:border-0"
                >
                  <td className="py-2 pr-4 text-white/80 whitespace-nowrap">
                    {jobNames[run.jobId] ?? run.jobId}
                  </td>
                  <td className="py-2 pr-4">
                    <span
                      className={`px-2 py-0.5 rounded-full text-xs font-medium border ${runStatusStyle(run.status).chip}`}
                    >
                      {run.status}
                    </span>
                  </td>
                  <td className="py-2 pr-4 text-white/60 whitespace-nowrap">
                    {formatRelativeTime(run.startedAt)}
                  </td>
                  <td className="py-2 pr-4 text-white/60 whitespace-nowrap">
                    {formatDuration(run.durationSeconds) ?? "—"}
                  </td>
                  <td className="py-2 pr-4 text-white/60 whitespace-nowrap">
                    {run.rowsLoaded !== null
                      ? run.rowsLoaded.toLocaleString()
                      : "—"}
                  </td>
                  <td
                    className="py-2 pr-4 text-red-400/80 max-w-[240px] truncate"
                    title={run.errorMessage ?? undefined}
                  >
                    {run.errorMessage ?? "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
